import React, { useState } from 'react';
import { Image, ImageBackground, Linking, StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAIcon, Disclaimer, DISCLAIMER_MODE, DNAText, Modal } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { isTablet } from 'react-device-detect';
import { DNAPopover } from 'src/components/DNA';
import { useUserTenant } from 'src/state/redux/selector/user';
import { getHostname, shouldBlockDomainMigration } from './domainMigration';

const BeaconLogo = require('../../../assets/images/BeaconMedispendLoginLogo.svg');
const MigrationBackground = require('../../../assets/images/LoginBackground.svg');
const PWAInstallImage = require('../../../assets/images/pwa_install_new.png');
const OfflineSyncedImage = require('../../../assets/images/offline_synced.png');

const styles = StyleSheet.create({
  backdrop: { backgroundColor: 'transparent' },
  modal: { alignItems: 'center', height: '100vh', justifyContent: 'center', width: '100vw' },
  dynamicViewport: { height: '100dvh' },
  card: { backgroundColor: colors['color-white'], borderRadius: 26, height: 549, padding: 40, width: 500 },
  instructionCard: { backgroundColor: colors['color-white'], borderRadius: 12, height: 658, padding: 40, width: 1000 },
  ipadInstructionCard: {
    backgroundColor: colors['color-white'], borderRadius: 12, height: 300, padding: 16, width: 750,
  },
  logo: { height: 63, width: 143 },
  title: { fontSize: 32 },
  content: { fontSize: 15 },
  domain: { color: colors['color-text-primary'], fontSize: 15, lineHeight: 18 },
  oldDomain: { color: colors['color-ui2-neutral-500'], textDecorationLine: 'line-through' },
  newDomain: { fontWeight: '700' },
  urlAnchor: { width: '100%' },
  tooltip: { maxWidth: 300 },
  buttonGroup: { marginTop: 24 },
  privacy: {
    color: colors['color-gray-400'],
    fontSize: 14,
    textAlign: 'center',
  },
  copyright: {
    bottom: 'max(24px, calc(12px + env(safe-area-inset-bottom, 0px)))',
    color: colors['color-gray-400'],
    fontSize: 13,
    position: 'absolute',
  },
  instructionHeader: { fontSize: 16 },
  instruction: { borderTopColor: colors['color-gray-80'], borderTopWidth: 1 },
  instructionRow: { borderBottomColor: colors['color-gray-80'], borderBottomWidth: 1, paddingVertical: 12 },
  instructionNumber: { marginRight: 20 },
  instructionText: { fontSize: 15 },
  instructionImage: { height: 115, width: 320 },
  ipadTitle: { fontSize: 19 },
  ipadStepText: { fontSize: 18 },
});

const getMigrationUrl = (newDomain: string): string => {
  const currentUrl = new URL(window.location.href);
  const destinationUrl = new URL(newDomain);

  destinationUrl.pathname = currentUrl.pathname;
  destinationUrl.search = currentUrl.search;
  destinationUrl.hash = currentUrl.hash;

  return destinationUrl.toString();
};

const getDisplayedUrl = (url: string): string => url.replace(/^https?:\/\//i, '');

interface DomainMovedContentProps {
  newDomain: string;
  onInstall: () => void;
  onPrivacy: () => void;
}

const DomainMovedContent: React.FC<DomainMovedContentProps> = ({ newDomain, onInstall, onPrivacy }) => (
  <DNABox appearance="col" spacing="between" fill>
    <Image source={BeaconLogo} style={styles.logo} resizeMode="contain" />
    <DNABox appearance="col" spacing="md">
      <DNAText h3 style={styles.title}>Beacon has a new home</DNAText>
      <DNAText style={styles.content}>
        We've moved to a new domain. Please continue to the new site and update your bookmarks — your account,
        login, and data are unchanged.
      </DNAText>
      <DNABox fill alignY="center">
        <DNABox>
          <DNAPopover fill>
            <DNAPopover.Anchor style={styles.urlAnchor}>
              <DNAText
                ellipsizeMode="middle"
                numberOfLines={1}
                style={[styles.domain, styles.oldDomain]}
                testID="migration-source-url"
              >
                {getDisplayedUrl(window.location.href)}
              </DNAText>
            </DNAPopover.Anchor>
            <DNAPopover.Content offset={2}>
              <DNAText colorMode="dark" style={styles.tooltip} testID="migration-source-url-tooltip">
                {window.location.href}
              </DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        </DNABox>
        <DNAText style={styles.domain}>{'  →  '}</DNAText>
        <DNABox fill>
          <DNAPopover fill>
            <DNAPopover.Anchor style={styles.urlAnchor}>
              <DNAText
                ellipsizeMode="middle"
                numberOfLines={1}
                style={[styles.domain, styles.newDomain]}
                testID="migration-destination-url"
              >
                {getDisplayedUrl(getMigrationUrl(newDomain))}
              </DNAText>
            </DNAPopover.Anchor>
            <DNAPopover.Content offset={2}>
              <DNAText colorMode="dark" style={styles.tooltip} testID="migration-destination-url-tooltip">
                {getMigrationUrl(newDomain)}
              </DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        </DNABox>
      </DNABox>
      <DNABox appearance="col" spacing="md" style={styles.buttonGroup}>
        <DNAButton size="lg" stretch onPress={() => Linking.openURL(getMigrationUrl(newDomain))} status="primary">
          Continue to new site
        </DNAButton>
        <DNAButton size="lg" stretch appearance="outline" status="tertiary" iconLeft="download" onPress={onInstall}>
          Re-install {isTablet ? 'on iPad home screen' : 'desktop app'}
        </DNAButton>
      </DNABox>
    </DNABox>
    <DNAText style={styles.privacy} onPress={onPrivacy}>Data Privacy Policy</DNAText>
  </DNABox>
);

interface InstallInstructionsContentProps { newDomain: string; onClose: () => void; }

const IpadInstallInstructionsContent: React.FC<InstallInstructionsContentProps> = ({ newDomain, onClose }) => (
  <DNABox appearance="col" spacing="sm" fill>
    <DNABox spacing="between" alignY="center">
      <DNAText bold style={styles.ipadTitle}>Install Beacon for iPad</DNAText>
      <DNAButton appearance="ghost" status="subtle" iconLeft="x" onPress={onClose} />
    </DNABox>
    <DNABox appearance="col">
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-one-fill" size="lg" style={styles.instructionNumber} />
        <DNAText style={styles.ipadStepText}>
          Ensure you are connected to the internet. Do not use a private browser.
        </DNAText>
      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-two-fill" size="lg" style={styles.instructionNumber}/>
        <DNABox spacing="between" alignY="center" fill>
          <DNAText style={styles.ipadStepText}>
            Open Beacon's new domain: <DNAText bold>{getHostname(newDomain)}</DNAText>
          </DNAText>
          <DNAButton size="sm" status="primary" onPress={() => Linking.openURL(newDomain)}>
            Open domain
          </DNAButton>
        </DNABox>
      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-three-fill" size="lg" style={styles.instructionNumber} />
        <DNAText style={styles.ipadStepText}>Tap on the share icon located next to the URL bar.</DNAText>
      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-four-fill" size="lg" style={styles.instructionNumber} />
        <DNAText style={styles.ipadStepText}>Select the "Add to Home Screen" option.</DNAText>
      </DNABox>
    </DNABox>
  </DNABox>
);

const InstallInstructionsContent: React.FC<InstallInstructionsContentProps> = ({ newDomain: _newDomain, onClose }) => (
  <DNABox appearance="col" spacing="sm" fill>
    <DNABox spacing="between" alignY="center">
      <DNAText bold style={styles.instructionHeader}>Install Beacon {isTablet ? 'iPad' : 'desktop'} app</DNAText>
      <DNAButton appearance="ghost" status="subtle" iconLeft="x" onPress={onClose} />
    </DNABox>
    <DNAText style={styles.content} bold>
      To install the Beacon application, please follow the steps below. Beacon&apos;s application provides users access
      to all their content when Wi-Fi is unavailable.
    </DNAText>
    <DNABox appearance="col" style={styles.instruction}>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-one-fill" size="lg" style={styles.instructionNumber} />
        <DNAText style={styles.instructionText}>
          Ensure you are connected to the internet. Do not use a private browser.
        </DNAText>
      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-two-fill" size="lg" style={styles.instructionNumber} />
        <DNABox fill spacing="between" alignY="center">
          <DNAText style={styles.instructionText}>
            Click the “Activate” button to enable document sync for offline use. This screen will refresh.
          </DNAText>
          <DNAButton size="sm" status="primary" onPress={() => undefined}>Activate</DNAButton>
        </DNABox>
      </DNABox>
      <DNABox style={styles.instructionRow} spacing="between" alignY="center">
        <DNABox>
          <DNAIcon.Styled name="number-three-fill" size="lg" style={styles.instructionNumber} />

          <DNAText style={styles.instructionText}>
            After offline mode is enabled (this only needs to be done once), an install icon will
            {'\n'}
            appear in the address bar.
          </DNAText>
        </DNABox>
        <Image source={PWAInstallImage} resizeMode="contain" style={styles.instructionImage} />

      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-four-fill" size="lg" style={styles.instructionNumber} />
        <DNAText style={styles.instructionText}>
          Click the icon to install the Beacon application on your laptop. Once installed, you will be able to access
          all features and functionalities through the Beacon Application for Laptop.
        </DNAText>
      </DNABox>
      <DNABox style={styles.instructionRow}>
        <DNAIcon.Styled name="number-five-fill" size="lg" style={styles.instructionNumber} />
        <DNABox fill>
          <DNAText style={styles.instructionText}>
            Once your content is fully synced, you will see a green check mark, and you're all set for offline use
            through the Beacon application!
          </DNAText>
          <Image source={OfflineSyncedImage} resizeMode="contain" style={styles.instructionImage} />
        </DNABox>
      </DNABox>
    </DNABox>
  </DNABox>
);

const DomainMigrationModal: React.FC = () => {
  const tenant = useUserTenant();
  const [showInstructions, setShowInstructions] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const preferredDomain = tenant?.config?.preferredBeaconDomain?.trim();
  const shouldBlock = shouldBlockDomainMigration(preferredDomain);

  if (!shouldBlock || !preferredDomain) return null;

  const newDomain = preferredDomain.startsWith('http') ? preferredDomain : `https://${preferredDomain}`;

  return (
    <>
      {showPrivacy && <Disclaimer mode={DISCLAIMER_MODE.Privacy} onClose={() => setShowPrivacy(false)} />}
      <Modal visible backdropStyle={styles.backdrop}>
        <ImageBackground
          source={MigrationBackground}
          style={[styles.modal, styles.dynamicViewport]}
          resizeMode="cover"
          testID="domain-migration-viewport"
        >
          <DNABox
            style={showInstructions
              ? (isTablet ? styles.ipadInstructionCard : styles.instructionCard)
              : styles.card}
          >
            {showInstructions ? (
              isTablet
                ? <IpadInstallInstructionsContent newDomain={newDomain} onClose={() => setShowInstructions(false)} />
                : <InstallInstructionsContent newDomain={newDomain} onClose={() => setShowInstructions(false)} />
            ) : (
              <DomainMovedContent
                newDomain={newDomain}
                onInstall={() => setShowInstructions(true)}
                onPrivacy={() => setShowPrivacy(true)}
              />
            )}
          </DNABox>
          {!showInstructions && <DNAText style={styles.copyright}>Copyright {new Date().getFullYear()} MMIS</DNAText>}
        </ImageBackground>
      </Modal>
    </>
  );
};

export default DomainMigrationModal;
